import type { RootState } from "@/store"
import { TAGS, api } from "@/store/api.ts"
import { clearGameplayState, setSessionId } from "@/store/slices/gameplay"
import { clearMatchState, setMatchSession } from "@/store/slices/match"
import { IPaginationReq, IPaginationRes } from "@/types"
import {
  COMMON_BID_VARIANT_ID,
  IMatchBidVariant,
  IMatchConfirmSessionResponse,
  IMatchRoomPlayersCount,
  IMatchSearchResponse,
  IMatchSessionResponse
} from "."
import { deckEndpoints } from "../deckAPI"
import { effectsApi } from "../effectsAPI"
import { IGameplayLog, gameplayEndpoints } from "../gameplayAPI"
import { playerEndpoints } from "../playerAPI"
import { socketMatch, socketMatchVariants } from "./match.socket"

const basePath = "/match"

const matchAPI = api.enhanceEndpoints({ addTagTypes: [TAGS.MATCH] })

export const matchEndpoints = matchAPI.injectEndpoints({
  endpoints: (builder) => ({
    getMatchSession: builder.query<
      IMatchSessionResponse,
      { sessionId: string; playerId: string }
    >({
      query: ({ sessionId }) => ({
        url: `${basePath}/session/${sessionId}`,
        method: "GET"
      }),
      providesTags: (_, __, { sessionId }) => [
        { type: TAGS.MATCH, id: `MATCH_SESSION` },
        { type: TAGS.MATCH, id: `MATCH_SESSION:${sessionId}` }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(setMatchSession(data))
          dispatch(setSessionId(data.sessionId))
        } catch {
          // query failed, no action needed
        }
      },
      async onCacheEntryAdded(
        { playerId },
        { updateCachedData, cacheEntryRemoved, dispatch }
      ) {
        socketMatch.setUpdateMatchSessionData(updateCachedData)
        socketMatch.setDispatch(dispatch)
        socketMatch.setPlayerId(playerId)

        socketMatch.init(playerId)
        socketMatch.addSub()

        const unsubscribeSessionFinished =
          socketMatch.bindSessionFinishedListener()

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeSessionFinished?.()

          socketMatch.removeSub()
        }
      }
    }),
    getMyMatchSession: builder.query<
      IMatchSessionResponse,
      { playerId: string }
    >({
      query: () => ({
        url: `${basePath}/my-session`,
        method: "GET"
      }),
      providesTags: [{ type: TAGS.MATCH, id: `MY_SESSION` }],
      async onQueryStarted({ playerId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(setMatchSession(data))
          dispatch(setSessionId(data.sessionId))
          dispatch(
            matchAPI.util.upsertQueryData(
              "getMatchSession" as never,
              { sessionId: data.sessionId, playerId } as never,
              data as never
            )
          )
        } catch {
          // query failed, no action needed
        }
      },
      async onCacheEntryAdded({ playerId }, { cacheEntryRemoved, dispatch }) {
        socketMatch.setDispatch(dispatch)
        socketMatch.setPlayerId(playerId)

        socketMatch.init(playerId)
        socketMatch.addSub()

        const unsubscribeMatchFound = socketMatch.bindMatchFoundListener()
        const unsubscribeSessionReady = socketMatch.bindSessionReadyListener()

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeMatchFound?.()
          unsubscribeSessionReady?.()

          socketMatch.removeSub()
        }
      }
    }),
    matchSearch: builder.mutation<
      IMatchSearchResponse,
      { variant?: string } | void
    >({
      query: (params) => ({
        url: `${basePath}/search`,
        method: "POST",
        params
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled, getState }) {
        const prevMatchSession = (getState() as RootState).match.matchSession

        if (prevMatchSession?.status === "FINISHED") {
          dispatch(clearMatchState())
          dispatch(clearGameplayState())
          dispatch(
            matchAPI.util.invalidateTags([
              { type: TAGS.MATCH, id: "MY_SESSION" },
              { type: TAGS.MATCH, id: "CONFIRM_SESSION" }
            ])
          )
        }

        let prevValue: any = {
          inQueue: false,
          matchBidVariant: undefined
        }

        try {
          dispatch(
            playerEndpoints.util.updateQueryData(
              "getPlayerProfile",
              undefined,
              (draft) => {
                prevValue = {
                  inQueue: draft.inQueue,
                  matchBidVariant: draft.matchBidVariant
                }

                draft.inQueue = true
                draft.matchBidVariant = params?.variant || COMMON_BID_VARIANT_ID
              }
            )
          )

          const { data } = await queryFulfilled

          if (data.status !== "SEARCHING") {
            dispatch(
              playerEndpoints.util.updateQueryData(
                "getPlayerProfile",
                undefined,
                (draft) => {
                  draft.inQueue = prevValue.inQueue
                  draft.matchBidVariant = prevValue.matchBidVariant
                }
              )
            )
          }
        } catch (error) {
          dispatch(
            playerEndpoints.util.updateQueryData(
              "getPlayerProfile",
              undefined,
              (draft) => {
                draft.inQueue = prevValue.inQueue
                draft.matchBidVariant = prevValue.matchBidVariant
              }
            )
          )
          console.error(error)
        }
      }
    }),
    matchCancel: builder.mutation<
      {
        status: "CANCELLED"
      },
      void
    >({
      query: () => ({
        url: `${basePath}/cancel`,
        method: "POST"
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          if (data.status === "CANCELLED") {
            dispatch(
              playerEndpoints.util.updateQueryData(
                "getPlayerProfile",
                undefined,
                (draft) => {
                  draft.inQueue = false
                  draft.matchBidVariant = undefined
                }
              )
            )
          }
        } catch (error) {
          console.error(error)
        }
      }
    }),
    matchDelete: builder.mutation<void, string>({
      query: (sessionId) => ({
        url: `${basePath}/delete/${sessionId}`,
        method: "DELETE"
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled

        dispatch(clearMatchState())
        dispatch(clearGameplayState())
        dispatch(deckEndpoints.util.invalidateTags([{ type: TAGS.DECK }]))
        dispatch(effectsApi.util.invalidateTags([{ type: TAGS.EFFECTS }]))
        dispatch(matchEndpoints.util.invalidateTags([{ type: TAGS.MATCH }]))
        dispatch(playerEndpoints.util.invalidateTags([{ type: TAGS.PLAYER }]))
        dispatch(
          gameplayEndpoints.util.invalidateTags([{ type: TAGS.GAMEPLAY }])
        )
      }
    }),
    matchResign: builder.mutation<void, string>({
      query: (sessionId) => ({
        url: `${basePath}/resign/${sessionId}`,
        method: "PATCH"
      })
    }),
    getMatchAverageSearchTime: builder.query<any, void>({
      query: () => ({
        url: `${basePath}/average-search-time`,
        method: "GET"
      })
    }),

    getMatchLogsHistory: builder.query<
      IGameplayLog[],
      { sessionId: string; limit: number; turn: number }
    >({
      query: ({ sessionId, limit, turn }) => ({
        url: `${basePath}/history/logs/${sessionId}`,
        method: "GET",
        params: {
          limit,
          turn
        }
      }),
      providesTags: (_, __, { sessionId, limit, turn }) => [
        { type: TAGS.MATCH, id: `LOGS` },
        { type: TAGS.MATCH, id: `LOGS_HISTORY` },
        {
          type: TAGS.MATCH,
          id: `LOGS_HISTORY:${sessionId}:${limit}:${turn}`
        }
      ]
    }),

    getMatchBidVariants: builder.query<
      IPaginationRes<IMatchBidVariant>,
      IPaginationReq
    >({
      query: (params) => ({
        url: `${basePath}/bid-variants`,
        method: "GET",
        params
      })
    }),

    getMatchRoomPlayersCount: builder.query<IMatchRoomPlayersCount, void>({
      query: (params) => ({
        url: `${basePath}/queue/count`,
        method: "GET",
        params
      }),
      providesTags: [{ type: TAGS.MATCH, id: "ROOM_PLAYERS_COUNT" }],
      async onCacheEntryAdded(
        _,
        { updateCachedData, cacheEntryRemoved, dispatch }
      ) {
        socketMatchVariants.setUpdateRoomPlayersCountData(updateCachedData)
        socketMatchVariants.setDispatch(dispatch)

        socketMatchVariants.init()
        socketMatchVariants.addSub()

        const unsubscribeQueueUpdated =
          socketMatchVariants.bindQueueUpdatedListener()

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeQueueUpdated?.()
          socketMatchVariants.removeSub()
        }
      }
    }),

    getMatchConfirmSession: builder.query<
      IMatchConfirmSessionResponse,
      { sessionId: string; playerId: string }
    >({
      query: ({ sessionId }) => ({
        url: `${basePath}/confirm/${sessionId}`,
        method: "GET"
      }),
      providesTags: (_, __, { sessionId }) => [
        { type: TAGS.MATCH, id: `CONFIRM_SESSION` },
        { type: TAGS.MATCH, id: `CONFIRM_SESSION:${sessionId}` }
      ],
      async onCacheEntryAdded(
        { playerId },
        { updateCachedData, cacheEntryRemoved, dispatch }
      ) {
        socketMatch.setUpdateMatchConfirmSessionData(updateCachedData)
        socketMatch.setDispatch(dispatch)
        socketMatch.init(playerId)
        socketMatch.addSub()
        const unsubscribeSessionConfirmed =
          socketMatch.bindSessionConfirmListener()
        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeSessionConfirmed?.()
          socketMatch.removeSub()
        }
      }
    }),

    matchConfirm: builder.mutation<
      void,
      { sessionId: string; accept: boolean }
    >({
      query: (data) => ({
        url: `${basePath}/confirm`,
        method: "POST",
        data
      })
    })
  })
})

export { matchAPI }

export const {
  useMatchSearchMutation,
  useMatchCancelMutation,
  useGetMatchSessionQuery,
  useGetMyMatchSessionQuery,
  useMatchDeleteMutation,
  useMatchResignMutation,
  useGetMatchAverageSearchTimeQuery,
  useGetMatchLogsHistoryQuery,
  useGetMatchBidVariantsQuery,
  useGetMatchRoomPlayersCountQuery,
  useGetMatchConfirmSessionQuery,
  useMatchConfirmMutation
} = matchEndpoints
